import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { statsService, authService, uploadService, rewardService } from '../api/services';
import LevelBadge from '../components/LevelBadge';
import PasswordChangeModal from '../components/PasswordChangeModal';
import AchievementsSection from '../components/AchievementsSection';
import CustomBadgesSection from '../components/CustomBadgesSection';
import './UserProfilePage.css';

// 兌換狀態顯示文字
const REDEMPTION_STATUS_LABEL = { PENDING: '待審核', APPROVED: '已同意', REJECTED: '已拒絕', CANCELLED: '已取消' };

const UserProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ account: '', name: '', email: '', joined_at: '', nickname: '', avatar_url: '', profile_banner_url: '', profile_banner_opacity: '0.4', contact_info: '', line_id: '', code_theme_preference: 'dark' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [imageEditor, setImageEditor] = useState(null);
  const formatDateInputValue = (value) => (value ? String(value).split('T')[0] : '');

  const createCroppedImage = async (file, purpose, zoom) => {
    const imageUrl = URL.createObjectURL(file);
    const img = new Image();
    img.src = imageUrl;
    await img.decode();
    const outputWidth = purpose === 'avatar' ? 512 : 1600;
    const outputHeight = purpose === 'avatar' ? 512 : 400;
    const targetRatio = outputWidth / outputHeight;
    let sourceWidth = img.width;
    let sourceHeight = img.height;
    if (sourceWidth / sourceHeight > targetRatio) sourceWidth = sourceHeight * targetRatio;
    else sourceHeight = sourceWidth / targetRatio;
    sourceWidth = Math.max(1, sourceWidth / zoom);
    sourceHeight = Math.max(1, sourceHeight / zoom);
    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    canvas.getContext('2d').drawImage(img, (img.width - sourceWidth) / 2, (img.height - sourceHeight) / 2, sourceWidth, sourceHeight, 0, 0, outputWidth, outputHeight);
    URL.revokeObjectURL(imageUrl);
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await statsService.getMe();
        setStats(res.data);
        // 個人兌換紀錄（顯示於點數區塊下方）；失敗不影響統計呈現
        try {
          const redRes = await rewardService.myRedemptions();
          setRedemptions(redRes.data?.items || []);
        } catch (e) { /* 兌換紀錄載入失敗不阻斷個人頁 */ }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        account: user.account || '',
        name: user.name || '',
        email: user.email || '',
        joined_at: formatDateInputValue(user.joined_at),
        nickname: user.nickname || '',
        avatar_url: user.avatar_url || '',
        profile_banner_url: user.profile_banner_url || '',
        profile_banner_opacity: user.profile_banner_opacity || '0.4',
        contact_info: user.contact_info || '',
        line_id: user.line_id || '',
        code_theme_preference: user.code_theme_preference || 'dark',
      });
    }
  }, [user]);

  const handleSave = async () => {
    try {
      setError('');
      if (!form.account.trim() || !form.name.trim() || !form.email.trim()) {
        setError('請填寫帳號、真實姓名與電子信箱');
        return;
      }
      // 業務邏輯：一般使用者可自行維護個人與職涯欄位，但 role/status 權限與帳號狀態仍保留給 Admin 管理。
      await authService.updateProfile({ ...form, joined_at: formatDateInputValue(form.joined_at) });
      if (refreshUser) await refreshUser();
      setEditing(false);
      setSuccess('個人資料已更新！');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const openImageEditor = (purpose, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('請選擇圖片檔案');
      return;
    }
    setImageEditor({ purpose, file, previewUrl: URL.createObjectURL(file), zoom: 1 });
  };

  const handleUploadEditedImage = async () => {
    try {
      setError('');
      const blob = await createCroppedImage(imageEditor.file, imageEditor.purpose, imageEditor.zoom);
      const formData = new FormData();
      formData.append('purpose', imageEditor.purpose);
      formData.append('files', blob, `${imageEditor.purpose}.jpg`);
      const res = await uploadService.upload(formData);
      const url = res.data?.[0]?.url;
      if (imageEditor.purpose === 'avatar') setForm(p => ({ ...p, avatar_url: url || p.avatar_url }));
      if (imageEditor.purpose === 'banner') setForm(p => ({ ...p, profile_banner_url: url || p.profile_banner_url }));
      if (refreshUser) await refreshUser();
      URL.revokeObjectURL(imageEditor.previewUrl);
      setImageEditor(null);
      setSuccess('圖片已上傳，請確認資料後儲存。');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  if (loading) return <div className="container mt-5">載入中...</div>;

  const avatarChar = (user?.nickname || user?.name || '?').charAt(0).toUpperCase();

  return (
    <div className="user-profile-page">
      <div className="container">
        <div className="profile-header" style={{ '--profile-banner-image': user?.profile_banner_url ? `url(${user.profile_banner_url})` : 'none', '--profile-banner-opacity': user?.profile_banner_opacity || '0.4' }}>
          {user?.avatar_url ? (
            <img className="profile-avatar-img" src={user.avatar_url} alt="avatar" />
          ) : (
            <div className="profile-avatar">{avatarChar}</div>
          )}
          <div className="profile-info">
            <h1>{user?.nickname || user?.name}</h1>
            {user?.nickname && <p className="profile-name-sub">{user.name}</p>}
            <p className="profile-account">@{user?.account}</p>
            <p className="profile-email">{user?.email}</p>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        {showPasswordModal && (
          <PasswordChangeModal
            onClose={() => setShowPasswordModal(false)}
            onSuccess={() => {
              setSuccess('密碼已更新！');
              setTimeout(() => setSuccess(''), 3000);
            }}
          />
        )}

        <div className="profile-detail-section">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <h3 style={{margin:0}}>個人資料</h3>
            {!editing && <button className="btn btn-sm btn-primary" onClick={() => setEditing(true)}>編輯</button>}
          </div>
          {editing ? (
            <div className="profile-edit-form">
              <label>帳號</label>
              <input className="form-control" value={form.account} onChange={e => setForm(p => ({...p, account: e.target.value}))} placeholder="登入帳號" />
              <label>真實姓名</label>
              <input className="form-control" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="真實姓名" />
              <label>電子信箱</label>
              <input className="form-control" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="name@example.com" />
              <label>入職日</label>
              <input className="form-control" type="date" value={form.joined_at} onChange={e => setForm(p => ({...p, joined_at: e.target.value}))} />
              <label>暱稱</label>
              <input className="form-control" value={form.nickname} onChange={e => setForm(p => ({...p, nickname: e.target.value}))} placeholder="顯示暱稱" />
              <label>頭像 URL</label>
              <input className="form-control" value={form.avatar_url} onChange={e => setForm(p => ({...p, avatar_url: e.target.value}))} placeholder="https://..." />
              <label>上傳與裁切頭像</label>
              <input className="form-control" type="file" accept="image/*" onChange={e => openImageEditor('avatar', e.target.files?.[0])} />
              <label>Profile 背景牆 URL</label>
              <input className="form-control" value={form.profile_banner_url} onChange={e => setForm(p => ({...p, profile_banner_url: e.target.value}))} placeholder="https://..." />
              <label>上傳背景牆圖片</label>
              <input className="form-control" type="file" accept="image/*" onChange={e => openImageEditor('banner', e.target.files?.[0])} />
              <label>背景透明度 {form.profile_banner_opacity}</label>
              <input className="form-control" type="range" min="0.3" max="0.5" step="0.05" value={form.profile_banner_opacity} onChange={e => setForm(p => ({...p, profile_banner_opacity: e.target.value}))} />
              <label>聯絡方式</label>
              <input className="form-control" value={form.contact_info} onChange={e => setForm(p => ({...p, contact_info: e.target.value}))} placeholder="電話 / Slack / ..." />
              <label>Line ID</label>
              <input className="form-control" value={form.line_id} onChange={e => setForm(p => ({...p, line_id: e.target.value}))} placeholder="Line ID" />
              <label>程式碼顯示主題</label>
              <select className="form-control" value={form.code_theme_preference} onChange={e => setForm(p => ({...p, code_theme_preference: e.target.value}))}>
                <option value="dark">暗色（預設）</option>
                <option value="light">亮色</option>
              </select>
              <div className="btn-group" style={{marginTop:12}}>
                <button className="btn btn-sm btn-primary" onClick={handleSave}>儲存</button>
                <button className="btn btn-sm btn-secondary" onClick={() => setEditing(false)}>取消</button>
              </div>
            </div>
          ) : (
            <div className="profile-detail-grid">
              <div className="detail-item"><span className="detail-label">帳號</span><span>@{user?.account}</span></div>
              <div className="detail-item"><span className="detail-label">真實姓名</span><span>{user?.name || '-'}</span></div>
              <div className="detail-item"><span className="detail-label">電子信箱</span><span>{user?.email || '-'}</span></div>
              <div className="detail-item"><span className="detail-label">入職日</span><span>{formatDateInputValue(user?.joined_at) || '-'}</span></div>
              <div className="detail-item"><span className="detail-label">暱稱</span><span>{user?.nickname || '-'}</span></div>
              <div className="detail-item"><span className="detail-label">背景透明度</span><span>{user?.profile_banner_opacity || '0.4'}</span></div>
              <div className="detail-item"><span className="detail-label">聯絡方式</span><span>{user?.contact_info || '-'}</span></div>
              <div className="detail-item"><span className="detail-label">Line ID</span><span>{user?.line_id || '-'}</span></div>
              <div className="detail-item"><span className="detail-label">程式碼主題</span><span>{user?.code_theme_preference === 'light' ? '亮色' : '暗色'}</span></div>
            </div>
          )}
        </div>

        <div className="profile-detail-section points-summary-section">
          <h3 style={{margin:'0 0 12px'}}>我的點數</h3>
          <div className="points-summary-grid">
            <div className="points-summary-card">
              <div className="points-summary-value">{stats?.pointsEarned || 0}</div>
              <div className="points-summary-label">累積點數</div>
            </div>
            <div className="points-summary-card">
              <div className="points-summary-value">{stats?.pointsRedeemed || 0}</div>
              <div className="points-summary-label">已兌換點數</div>
            </div>
            <div className="points-summary-card highlight">
              <div className="points-summary-value">{stats?.pointsRemaining || 0}</div>
              <div className="points-summary-label">剩餘點數</div>
            </div>
          </div>
          {redemptions.length > 0 && (
            <div className="points-redemption-history">
              <h4>我的兌換紀錄</h4>
              <ul>
                {redemptions.slice(0, 8).map((r) => (
                  <li key={r.id}>
                    <span className="pr-name">{r.reward_name} ×{r.quantity}</span>
                    <span className="pr-points">-{r.total_points}</span>
                    <span className={`pr-status pr-status-${r.status}`}>{REDEMPTION_STATUS_LABEL[r.status] || r.status}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {user?.id && (
          <div className="level-section">
            <LevelBadge userId={user.id} />
          </div>
        )}

        <div className="profile-detail-section">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <h3 style={{margin:0}}>密碼安全</h3>
            <button className="btn btn-sm btn-primary" onClick={() => setShowPasswordModal(true)}>修改密碼</button>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats?.postCount || 0}</div>
            <div className="stat-label">發文數</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.commentCount || 0}</div>
            <div className="stat-label">留言數</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.acceptedAnswerCount || 0}</div>
            <div className="stat-label">最佳解答</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.votesReceived || 0}</div>
            <div className="stat-label">被認同次數</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.votesGiven || 0}</div>
            <div className="stat-label">發出認同</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.resolvedPostsParticipated || 0}</div>
            <div className="stat-label">參與已解決</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats?.bountiesCompleted || 0}</div>
            <div className="stat-label">完成懸賞</div>
          </div>
        </div>

        <AchievementsSection />
        {user?.id && <CustomBadgesSection userId={user.id} />}
        {imageEditor && (
          <div className="image-editor-backdrop" role="dialog" aria-modal="true">
            <div className="image-editor-card">
              <h3>{imageEditor.purpose === 'avatar' ? '裁切頭像' : '調整背景牆'}</h3>
              <div className={`image-editor-preview ${imageEditor.purpose}`}>
                <img src={imageEditor.previewUrl} alt="preview" style={{ transform: `scale(${imageEditor.zoom})` }} />
              </div>
              <label>縮放 {imageEditor.zoom.toFixed(1)}x</label>
              <input type="range" min="1" max="3" step="0.1" value={imageEditor.zoom} onChange={e => setImageEditor(p => ({ ...p, zoom: Number(e.target.value) }))} />
              <div className="btn-group" style={{marginTop:12}}>
                <button className="btn btn-sm btn-primary" onClick={handleUploadEditedImage}>上傳圖片</button>
                <button className="btn btn-sm btn-secondary" onClick={() => { URL.revokeObjectURL(imageEditor.previewUrl); setImageEditor(null); }}>取消</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfilePage;

